import React, { useEffect } from 'react';
import { DebugData } from '../services/api';

interface DebugModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: DebugData | null;
  loading: boolean;
  price: number | null;
  sku: string;
}

const DebugModal: React.FC<DebugModalProps> = ({
  isOpen,
  onClose,
  data,
  loading,
  price,
  sku
}) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const formatPrice = (price: number | string | null | undefined) => {
    if (price === null || price === undefined) return 'N/A';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return 'N/A';
    return `${numPrice.toFixed(2)}€`;
  };

  const formatNumber = (value: number | string | null | undefined, decimals: number = 2) => {
    if (value === null || value === undefined) return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'N/A';
    return numValue.toFixed(decimals);
  };

  return (
    <div 
      className="modal show d-block" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Debug - {sku} - Preço: {price ? formatPrice(price) : 'N/A'}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="mt-2">Carregando dados de debug...</p>
              </div>
            ) : data ? (
              <div>
                {/* Sales Aggregated Data Section */}
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">
                    Dados de Vendas Agregadas ({data.sales_data.length} dias)
                  </h6>
                  {data.sales_data.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>Data</th>
                            <th>Quantidade</th>
                            <th>Receita Total</th>
                            <th>Nº Faturas</th>
                            <th>Média Items/Fatura</th>
                            <th>Preço Médio Calculado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.sales_data.map((item, index) => (
                            <tr key={index}>
                              <td className="fw-bold">{new Date(item.sale_date).toLocaleDateString('pt-PT')}</td>
                              <td className="text-center fw-bold text-primary">{item.daily_quantity}</td>
                              <td className="fw-bold text-success">{formatPrice(item.daily_revenue)}</td>
                              <td className="text-center">{item.daily_sales_count}</td>
                              <td className="text-center">{formatNumber(item.avg_items_per_invoice)}</td>
                              <td className="fw-bold text-info">{formatPrice(item.calculated_avg_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="alert alert-warning">
                      Nenhum dia com vendas encontrado para este preço.
                    </div>
                  )}
                </div>

                {/* Price Configuration Data Section */}
                <div>
                  <h6 className="fw-bold mb-3">
                    Configurações de Preço ({data.price_data.length} dias)
                  </h6>
                  {data.price_data.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>Data</th>
                            <th>Preço Praticado</th>
                            <th>ZMSR</th>
                            <th>ZPR1</th>
                            <th>ZPM3</th>
                            <th>Condição Ativa</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.price_data.map((item, index) => (
                            <tr key={index}>
                              <td className="fw-bold">{new Date(item.date).toLocaleDateString('pt-PT')}</td>
                              <td className="fw-bold text-success">{formatPrice(item.practiced_price)}</td>
                              <td className="text-center">{formatPrice(item.zmsr_price)}</td>
                              <td className="text-center">{formatPrice(item.zpr1_price)}</td>
                              <td className="text-center">{formatPrice(item.zpm3_price)}</td>
                              <td className="text-center">
                                <span className={`badge ${
                                  item.active_type === 'ZPM3' ? 'bg-success' :
                                  item.active_type === 'ZPR1' ? 'bg-warning' :
                                  item.active_type === 'ZMSR' ? 'bg-info' : 'bg-secondary'
                                }`}>
                                  {item.active_type || 'N/A'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="alert alert-warning">
                      Nenhuma configuração de preço encontrada para os dias com vendas.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="alert alert-danger">
                Erro ao carregar dados de debug.
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebugModal;